import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X, Image as ImageIcon, Globe, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import BusinessHoursInput from '@/components/BusinessHoursInput';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { resolveImageUrl } from '@/utils/imageUrl';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${window.location.origin}/api`;

// Service area options for online businesses
const SERVICE_AREAS = [
  { value: 'global', label: 'Global (Worldwide)' },
  { value: 'usa', label: 'United States Only' },
  { value: 'usa_canada', label: 'USA & Canada' },
  { value: 'north_america', label: 'North America' },
  { value: 'alabama', label: 'Alabama' },
  { value: 'alaska', label: 'Alaska' },
  { value: 'arizona', label: 'Arizona' },
  { value: 'arkansas', label: 'Arkansas' },
  { value: 'california', label: 'California' },
  { value: 'colorado', label: 'Colorado' },
  { value: 'connecticut', label: 'Connecticut' },
  { value: 'delaware', label: 'Delaware' },
  { value: 'florida', label: 'Florida' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'hawaii', label: 'Hawaii' },
  { value: 'idaho', label: 'Idaho' },
  { value: 'illinois', label: 'Illinois' },
  { value: 'indiana', label: 'Indiana' },
  { value: 'iowa', label: 'Iowa' },
  { value: 'kansas', label: 'Kansas' },
  { value: 'kentucky', label: 'Kentucky' },
  { value: 'louisiana', label: 'Louisiana' },
  { value: 'maine', label: 'Maine' },
  { value: 'maryland', label: 'Maryland' },
  { value: 'massachusetts', label: 'Massachusetts' },
  { value: 'michigan', label: 'Michigan' },
  { value: 'minnesota', label: 'Minnesota' },
  { value: 'mississippi', label: 'Mississippi' },
  { value: 'missouri', label: 'Missouri' },
  { value: 'montana', label: 'Montana' },
  { value: 'nebraska', label: 'Nebraska' },
  { value: 'nevada', label: 'Nevada' },
  { value: 'new_hampshire', label: 'New Hampshire' },
  { value: 'new_jersey', label: 'New Jersey' },
  { value: 'new_mexico', label: 'New Mexico' },
  { value: 'new_york', label: 'New York' },
  { value: 'north_carolina', label: 'North Carolina' },
  { value: 'north_dakota', label: 'North Dakota' },
  { value: 'ohio', label: 'Ohio' },
  { value: 'oklahoma', label: 'Oklahoma' },
  { value: 'oregon', label: 'Oregon' },
  { value: 'pennsylvania', label: 'Pennsylvania' },
  { value: 'rhode_island', label: 'Rhode Island' },
  { value: 'south_carolina', label: 'South Carolina' },
  { value: 'south_dakota', label: 'South Dakota' },
  { value: 'tennessee', label: 'Tennessee' },
  { value: 'texas', label: 'Texas' },
  { value: 'utah', label: 'Utah' },
  { value: 'vermont', label: 'Vermont' },
  { value: 'virginia', label: 'Virginia' },
  { value: 'washington', label: 'Washington' },
  { value: 'west_virginia', label: 'West Virginia' },
  { value: 'wisconsin', label: 'Wisconsin' },
  { value: 'wyoming', label: 'Wyoming' },
  { value: 'dc', label: 'Washington D.C.' },
];

export default function SubmitBusinessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const fileInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    website: '',
    category: '',
    is_online_only: false,
    service_area: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    image_url: '',
    business_hours: null,
    social_links: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catRes = await fetch(`${API}/categories`);
        if (catRes.ok) {
          const cats = await catRes.json();
          setCategories(cats);
        }

        // If editing, fetch business data
        if (editId) {
          const bizRes = await fetch(`${API}/businesses/user/list`, {
            credentials: 'include'
          });
          
          if (bizRes.ok) {
            const data = await bizRes.json();
            const businesses = data.businesses || data;
            const business = Array.isArray(businesses) 
              ? businesses.find(b => b.business_id === editId)
              : null;
            
            if (business) {
              setFormData({
                name: business.name || '',
                description: business.description || '',
                phone: business.phone || '',
                website: business.website || '',
                category: business.category || '',
                is_online_only: business.is_online_only || false,
                service_area: business.service_area || '',
                address: business.address || '',
                city: business.city || '',
                state: business.state || '',
                zip_code: business.zip_code || '',
                image_url: business.image_url || '',
                business_hours: business.business_hours || null,
                social_links: business.social_links || {
                  facebook: '',
                  instagram: '',
                  twitter: '',
                  linkedin: ''
                }
              });
              if (business.image_url) {
                setImagePreview(resolveImageUrl(business.image_url));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [editId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, image_url: data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate based on business type
    if (formData.is_online_only) {
      if (!formData.service_area) {
        toast.error('Please select a service/shipping region for your online business');
        return;
      }
    } else {
      if (!formData.address) {
        toast.error('Please enter a street address for your physical business');
        return;
      }
    }

    setLoading(true);

    try {
      const url = editId 
        ? `${API}/businesses/${editId}`
        : `${API}/businesses`;
      
      const method = editId ? 'PUT' : 'POST';

      // Clean up data - remove address fields for online-only businesses
      const submitData = { ...formData };
      if (submitData.is_online_only) {
        submitData.address = null;
        submitData.city = null;
        submitData.state = null;
        submitData.zip_code = null;
        submitData.latitude = null;
        submitData.longitude = null;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit business');
      }

      toast.success(editId ? 'Business updated successfully!' : 'Business submitted for review!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit business. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="h-8 w-48 skeleton rounded mb-8" />
            <div className="space-y-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-12 skeleton rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-mono text-sm">Back to Dashboard</span>
            </Link>

            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
              {editId ? 'Edit' : 'Submit'} Business
            </p>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              {editId ? 'Update Your Business' : 'List Your Business'}
            </h1>
            <p className="text-zinc-500 mt-2">
              {editId 
                ? 'Update your business information below.'
                : 'Fill out the form below to submit your business for verification.'}
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Business Image</Label>
              <div className="relative">
                {imagePreview ? (
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                      data-testid="remove-image-button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {uploading && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full aspect-video rounded-lg border-2 border-dashed border-zinc-800 hover:border-zinc-700 bg-zinc-900/50 flex flex-col items-center justify-center gap-3 transition-colors"
                    data-testid="upload-image-button"
                  >
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-zinc-500" />
                    </div>
                    <div className="text-center">
                      <p className="text-zinc-400 text-sm">Click to upload an image</p>
                      <p className="text-zinc-600 text-xs mt-1">JPEG, PNG, WebP, GIF up to 5MB</p>
                    </div>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-file-input"
                />
              </div>
              
              {/* URL fallback */}
              <div className="flex items-center gap-3 mt-3">
                <span className="text-zinc-600 text-xs">or</span>
                <Input
                  name="image_url"
                  value={formData.image_url}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value && !imagePreview) {
                      setImagePreview(e.target.value);
                    }
                  }}
                  placeholder="Paste image URL"
                  className="flex-1 bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50 text-sm h-9"
                  data-testid="input-image-url"
                />
              </div>
            </div>

            {/* Business Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">
                Business Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your business name"
                className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                data-testid="input-name"
                required
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-zinc-300">
                Category <span className="text-red-400">*</span>
              </Label>
              <Select
                value={formData.category}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger 
                  className="bg-zinc-900/80 border-zinc-800 text-white focus:ring-white/20 focus:border-white/50"
                  data-testid="select-category"
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {categories.map((cat) => (
                    <SelectItem 
                      key={cat.category_id} 
                      value={cat.name}
                      className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                    >
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-zinc-300">
                Description <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your business..."
                rows={4}
                className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50 resize-none"
                data-testid="input-description"
                required
              />
            </div>

            {/* Online Business Toggle */}
            <div className="glass-card rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                    formData.is_online_only 
                      ? 'bg-purple-500/20 text-purple-400' 
                      : 'bg-zinc-800 text-zinc-500'
                  }`}>
                    <Globe className="w-5 h-5" />
                  </div>
                  <div>
                    <Label className="text-white font-medium">This is an Online Business</Label>
                    <p className="text-zinc-500 text-sm mt-0.5">
                      Check this if your business operates online without a physical storefront
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_online_only}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({
                      ...prev,
                      is_online_only: checked,
                      // Clear the opposite type's required field
                      ...(checked ? { address: '', city: '', state: '', zip_code: '' } : { service_area: '' })
                    }));
                  }}
                  data-testid="toggle-online-business"
                />
              </div>
            </div>

            {/* Conditional: Physical Address OR Service Area */}
            <AnimatePresence mode="wait">
              {formData.is_online_only ? (
                <motion.div
                  key="service-area"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* Service Area */}
                  <div className="space-y-2">
                    <Label htmlFor="service_area" className="text-zinc-300 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-purple-400" />
                      Shipping/Service Region <span className="text-red-400">*</span>
                    </Label>
                    <Select
                      value={formData.service_area}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, service_area: value }))}
                    >
                      <SelectTrigger 
                        className="bg-zinc-900/80 border-zinc-800 text-white focus:ring-white/20 focus:border-white/50"
                        data-testid="select-service-area"
                      >
                        <SelectValue placeholder="Where do you ship/serve?" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                        {SERVICE_AREAS.map((area) => (
                          <SelectItem 
                            key={area.value} 
                            value={area.label}
                            className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                          >
                            {area.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-zinc-600 text-xs">
                      Select the geographic area where you provide your services or ship products
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="physical-address"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* Address */}
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-zinc-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-400" />
                      Street Address <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="123 Main Street"
                      className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                      data-testid="input-address"
                      required={!formData.is_online_only}
                    />
                  </div>

                  {/* City, State, Zip */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-zinc-300">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="City"
                        className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                        data-testid="input-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state" className="text-zinc-300">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="State"
                        className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                        data-testid="input-state"
                      />
                    </div>
                    <div className="space-y-2 col-span-2 md:col-span-1">
                      <Label htmlFor="zip_code" className="text-zinc-300">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleChange}
                        placeholder="12345"
                        className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                        data-testid="input-zip"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-zinc-300">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                data-testid="input-phone"
              />
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website" className="text-zinc-300">Website</Label>
              <Input
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://yourbusiness.com"
                className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                data-testid="input-website"
              />
            </div>

            {/* Social Links */}
            <div className="space-y-4">
              <Label className="text-zinc-300">Social Media Links (Optional)</Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  name="facebook"
                  value={formData.social_links?.facebook || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, facebook: e.target.value }
                  }))}
                  placeholder="Facebook URL"
                  className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                  data-testid="input-facebook"
                />
                <Input
                  name="instagram"
                  value={formData.social_links?.instagram || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, instagram: e.target.value }
                  }))}
                  placeholder="Instagram URL"
                  className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                  data-testid="input-instagram"
                />
                <Input
                  name="twitter"
                  value={formData.social_links?.twitter || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, twitter: e.target.value }
                  }))}
                  placeholder="Twitter/X URL"
                  className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                  data-testid="input-twitter"
                />
                <Input
                  name="linkedin"
                  value={formData.social_links?.linkedin || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, linkedin: e.target.value }
                  }))}
                  placeholder="LinkedIn URL"
                  className="bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:ring-white/20 focus:border-white/50"
                  data-testid="input-linkedin"
                />
              </div>
            </div>

            {/* Business Hours */}
            <div className="glass-card rounded-xl p-6">
              <BusinessHoursInput
                value={formData.business_hours}
                onChange={(hours) => setFormData(prev => ({ ...prev, business_hours: hours }))}
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading || uploading}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-business-button"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    {editId ? 'Updating...' : 'Submitting...'}
                  </>
                ) : (
                  editId ? 'Update Business' : 'Submit for Review'
                )}
              </button>
            </div>

            {!editId && (
              <p className="text-center text-zinc-500 text-sm">
                Your business will be reviewed by our team before being listed publicly.
              </p>
            )}
          </motion.form>
        </div>
      </main>
    </div>
  );
}
